import React from 'react';
import { createPortal } from 'react-dom';
import SoldCloseModal from './SoldCloseModal';
import MarkAvailableModal from './MarkAvailableModal';

// Shared stage pill + "move to stage" dropdown. Used on the Deals page, the Underwriting page, and
// the Pipeline deal view. Moving to Sold/Closed opens the SoldCloseModal to confirm sold price +
// close date; moving to Available opens the MarkAvailableModal, because publishing a deal to the
// shared board requires a complete row (the server enforces the same list — see
// backend/lib/availableRequirements.js — so skipping the form just earns a 409). The backend (PUT /api/deals/:id/stage) gates stage changes to the deal owner / org
// admin, so pass readOnly for viewers who can't edit (e.g. a sales agent on the shared Pipeline).
// Keep STAGE_COLORS / CANONICAL_STAGES in sync with backend DEAL_STAGES.
export const STAGE_COLORS = {
  // Canonical stages (8) — the Deals-tab buckets
  'Available':       { bg: 'rgba(107, 103, 96,0.12)', color: '#6B6760' },
  'Tagged':          { bg: 'rgba(184, 153, 104,0.14)', color: '#B89968' },
  'Offer Sent':      { bg: 'rgba(20, 80, 74,0.15)',  color: '#14504A' },
  'Under Contract':  { bg: 'rgba(74, 124, 89,0.15)',   color: '#4A7C59' },
  'Sold':            { bg: 'rgba(74, 124, 89,0.18)',   color: '#4A7C59' },
  'Closed':          { bg: 'rgba(45, 76, 56,0.18)',    color: '#2D4C38' },
  'Dropped':         { bg: 'rgba(184, 84, 80,0.12)',   color: '#B85450' },
  'Dead':            { bg: 'rgba(184, 84, 80,0.12)',   color: '#B85450' },
  // Legacy aliases — historical (Salesforce) deal rendering only
  'Working':         { bg: 'rgba(184, 153, 104,0.14)', color: '#B89968' },
  'Offer Accepted':  { bg: 'rgba(74, 124, 89,0.18)',   color: '#4A7C59' },
  'Contract Sent':   { bg: 'rgba(20, 80, 74,0.15)',  color: '#14504A' },
  'Active':          { bg: 'rgba(107, 103, 96,0.15)',  color: '#6B6760' },
  'Dead/Dropped':    { bg: 'rgba(184, 84, 80,0.12)',   color: '#B85450' },
  'Cancelled':       { bg: 'rgba(184, 84, 80,0.12)',   color: '#B85450' },
  'Email Sent':      { bg: 'rgba(184, 153, 104,0.10)', color: '#B89968' },
  'Inspected':       { bg: 'rgba(20, 80, 74,0.10)',  color: '#14504A' },
  'CMA':             { bg: 'rgba(224, 165, 38,0.12)',  color: '#E0A526' },
  'New':             { bg: 'rgba(156, 150, 144,0.12)', color: '#6B6760' },
  'Pre Close':       { bg: 'rgba(107, 103, 96,0.12)',  color: '#6B6760' },
  'GM Approved':     { bg: 'rgba(74, 124, 89,0.10)',   color: '#4A7C59' },
  'MLS - Analyzing': { bg: 'rgba(224, 165, 38,0.10)',  color: '#E0A526' },
};
export const CANONICAL_STAGES = ['Available', 'Tagged', 'Offer Sent', 'Under Contract', 'Sold', 'Closed', 'Dropped', 'Dead'];

// Deal sources that publish to the board on their own terms and are never held to the required-field
// form. Mirrors EXEMPT_SOURCES in backend/lib/availableRequirements.js — the server is the authority;
// this copy only decides whether to bother opening the form.
export const EXEMPT_SOURCES = new Set(['sheet_sync', 'import', 'Salesforce']);

export default function StageBadge({ stage, dealId, deal, onChanged, readOnly = false }) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [confirmStage, setConfirmStage] = React.useState(null); // 'Sold' | 'Closed' → confirm modal
  const [publishing, setPublishing] = React.useState(false);    // 'Available' → required-fields form
  const [coords, setCoords] = React.useState(null);             // fixed-position box for the portalled menu
  const ref = React.useRef(null);      // the pill wrapper (anchor)
  const menuRef = React.useRef(null);  // the portalled menu

  // The menu is rendered in a portal on document.body so it can't be clipped by any
  // overflow:hidden ancestor (the Deals table container clipped it for bottom rows). Position it
  // from the pill's viewport rect, flipping upward when there isn't room below.
  const MENU_W = 200;
  const positionMenu = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const estHeight = 340; // ~9 stages + header; only used to decide flip direction
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openUp = spaceBelow < estHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, (openUp ? spaceAbove : spaceBelow) - margin);
    let left = r.left;
    if (left + MENU_W > window.innerWidth - margin) left = window.innerWidth - MENU_W - margin;
    if (left < margin) left = margin;
    setCoords(openUp
      ? { left, bottom: window.innerHeight - r.top + 4, maxHeight }
      : { left, top: r.bottom + 4, maxHeight });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    positionMenu();
    function onDoc(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function reposition() { positionMenu(); }
    document.addEventListener('mousedown', onDoc);
    // capture:true so we also catch scrolls inside the table/list container, not just window scroll
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, positionMenu]);

  async function changeStage(newStage) {
    if (newStage === stage || saving) return;
    if (newStage === 'Sold' || newStage === 'Closed') { setOpen(false); setConfirmStage(newStage); return; }
    // Going onto the shared board collects the required fields first. Bridge- and import-sourced
    // rows publish on their own terms, so they move straight through (the server exempts them too).
    if (newStage === 'Available' && !EXEMPT_SOURCES.has(deal?.deal_source)) {
      setOpen(false); setPublishing(true); return;
    }
    setSaving(true);
    try {
      const r = await fetch(`/api/deals/${dealId}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage: newStage }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || 'failed');
      setOpen(false);
      if (onChanged) onChanged(newStage);
    } catch (err) {
      alert(`Failed to update stage: ${err.message}`);
    }
    setSaving(false);
  }

  const s = STAGE_COLORS[stage] || { bg: 'rgba(156, 150, 144,0.15)', color: '#6B6760' };
  const pill = (
    <span title={readOnly ? undefined : 'Click to change status'} style={{
      padding: readOnly ? '5px 12px' : '6px 10px 6px 14px', borderRadius: 20,
      fontSize: 13, fontWeight: 700,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
      cursor: readOnly ? 'default' : 'pointer',
      // A visible outline + caret chip so users can tell this pill is an editable status control.
      border: readOnly ? '1px solid transparent' : `1px solid ${s.color}55`,
      display: 'inline-flex', alignItems: 'center', gap: 7,
    }}>
      {stage}
      {!readOnly && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, borderRadius: '50%', background: `${s.color}22`,
          fontSize: 11, lineHeight: 1, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
        }}>▾</span>
      )}
    </span>
  );

  if (readOnly || !dealId) return pill;

  return (
    <>
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
      {pill}
      {open && coords && createPortal(
        <div ref={menuRef} onClick={e => e.stopPropagation()} style={{
          position: 'fixed', left: coords.left, top: coords.top, bottom: coords.bottom,
          width: MENU_W, maxHeight: coords.maxHeight, overflowY: 'auto', zIndex: 3000,
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
          borderRadius: 8, padding: 4, boxShadow: '0 6px 20px rgba(26,23,20,0.12)',
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 8px 6px' }}>
            Move to stage…
          </div>
          {CANONICAL_STAGES.map(opt => {
            const m = STAGE_COLORS[opt];
            const active = opt === stage;
            return (
              <div key={opt} onClick={e => { e.stopPropagation(); changeStage(opt); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6,
                  cursor: active || saving ? 'default' : 'pointer',
                  background: active ? 'var(--navy)' : 'transparent', opacity: saving ? 0.5 : 1,
                }}
                onMouseOver={e => !active && !saving && (e.currentTarget.style.background = 'var(--navy)')}
                onMouseOut={e => !active && !saving && (e.currentTarget.style.background = 'transparent')}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: m.color, flex: 1 }}>{opt}</span>
                {active && <span style={{ fontSize: 9, color: 'var(--gray-500)' }}>current</span>}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </span>
    {confirmStage && (
      <SoldCloseModal dealId={dealId} stage={confirmStage} deal={deal}
        onClose={() => setConfirmStage(null)}
        onConfirmed={(st) => { setConfirmStage(null); if (onChanged) onChanged(st); }} />
    )}
    {publishing && (
      <MarkAvailableModal dealId={dealId} address={deal?.address}
        onClose={() => setPublishing(false)}
        onPublished={() => { setPublishing(false); if (onChanged) onChanged('Available'); }} />
    )}
    </>
  );
}
